import { useCallback, useMemo, useState } from 'react';
import type {
  StaffResponse,
  TreatmentPlanTemplateResponse,
  YearPlanResponse
} from '../api/generated';
import {
  DEFAULT_MAX_STAFF_LOAD_PERCENT,
  MAX_STAFF_LOAD_CEILING_PERCENT,
  MAX_STAFF_LOAD_FLOOR_PERCENT
} from '../constants/yearPlanSimulationConstants';
import {
  isSelectionRunnable,
  selectionEconomics,
  type SimulationSelectionEconomics,
  type SimulationTemplateSelection,
  type YearPlanSimulationInput
} from '../services/yearPlanSimulationService';
import { planPatientCount } from '../utils/ganttUtils';
import { findCurrentWeekIndex } from '../utils/weekClassifier';

export interface SimulationTemplateRowState {
  isSelected: boolean;
  courseCount: number;
  capacity: number;
}

// The selected treatment plans added up, as if every course is placed.
export interface SimulationTotals extends Omit<SimulationSelectionEconomics, 'revenuePerPatient'> {
  courseCount: number;
}

export interface UseYearPlanSimulationFormResult {
  templates: TreatmentPlanTemplateResponse[];
  getRow: (templateId: string) => SimulationTemplateRowState;
  updateRow: (templateId: string, patch: Partial<SimulationTemplateRowState>) => void;
  fromWeekNumber: number;
  toWeekNumber: number;
  setFromWeekNumber: (weekNumber: number) => void;
  setToWeekNumber: (weekNumber: number) => void;
  maxStaffLoadPercent: number;
  setMaxStaffLoadPercent: (percent: number) => void;
  isPeriodValid: boolean;
  isMaxStaffLoadValid: boolean;
  // A ticked row lacks its seats or its number of courses.
  hasIncompleteSelection: boolean;
  totals: SimulationTotals;
  canRun: boolean;
  buildInput: (staff: readonly StaffResponse[], bedCount: number) => YearPlanSimulationInput;
}

const EMPTY_ROW: SimulationTemplateRowState = { isSelected: false, courseCount: 0, capacity: 0 };

// A template already rolled out on the plan starts from the seats its courses
// hold there; a new one starts empty so nothing is planned from a guess.
function initialRows(
  yearPlan: YearPlanResponse,
  templates: readonly TreatmentPlanTemplateResponse[]
): Record<string, SimulationTemplateRowState> {
  const rows: Record<string, SimulationTemplateRowState> = {};
  for (const template of templates) {
    const existing = yearPlan.treatmentPlans.find(
      (p) => p.treatmentPlanTemplateId === template.id && p.weeks.length > 0
    );
    rows[template.id] = { ...EMPTY_ROW, capacity: existing ? planPatientCount(existing) : 0 };
  }
  return rows;
}

// The period opens on the current week when it falls in the plan's year, so a
// simulation does not fill weeks that have already gone by.
function initialFromWeekNumber(yearPlan: YearPlanResponse): number {
  const currentWeekNumber = findCurrentWeekIndex(yearPlan.weeks);
  return currentWeekNumber > 0 ? currentWeekNumber : (yearPlan.weeks[0]?.weekNumber ?? 1);
}

export function useYearPlanSimulationForm(
  yearPlan: YearPlanResponse,
  treatmentPlanTemplates: readonly TreatmentPlanTemplateResponse[]
): UseYearPlanSimulationFormResult {
  const templates = useMemo((): TreatmentPlanTemplateResponse[] => {
    const sorted = [...treatmentPlanTemplates];
    sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [treatmentPlanTemplates]);
  const [rows, setRows] = useState(() => initialRows(yearPlan, templates));
  const [fromWeekNumber, setFromWeekNumber] = useState(() => initialFromWeekNumber(yearPlan));
  const [toWeekNumber, setToWeekNumber] = useState(
    () => yearPlan.weeks[yearPlan.weeks.length - 1]?.weekNumber ?? 1
  );
  const [maxStaffLoadPercent, setMaxStaffLoadPercent] = useState(DEFAULT_MAX_STAFF_LOAD_PERCENT);

  const updateRow = useCallback(
    (templateId: string, patch: Partial<SimulationTemplateRowState>): void => {
      setRows((previous) => ({
        ...previous,
        [templateId]: { ...(previous[templateId] ?? EMPTY_ROW), ...patch }
      }));
    },
    []
  );

  // A template that arrived after the dialog opened has no row yet and reads
  // as an unselected, empty one.
  const getRow = useCallback(
    (templateId: string): SimulationTemplateRowState => rows[templateId] ?? EMPTY_ROW,
    [rows]
  );

  const selectedRows = useMemo(
    (): SimulationTemplateSelection[] =>
      templates.flatMap((template): SimulationTemplateSelection[] => {
        const row = rows[template.id];
        if (!row?.isSelected) return [];
        return [{ templateId: template.id, courseCount: row.courseCount, capacity: row.capacity }];
      }),
    [templates, rows]
  );
  const selections = useMemo(
    (): SimulationTemplateSelection[] => selectedRows.filter(isSelectionRunnable),
    [selectedRows]
  );
  // A ticked treatment plan still missing its seats or its courses blocks the
  // run, rather than being left out of it without a word.
  const hasIncompleteSelection = selections.length < selectedRows.length;

  const totals = useMemo(
    (): SimulationTotals =>
      selections.reduce<SimulationTotals>(
        (sum, selection) => {
          const template = templates.find((t) => t.id === selection.templateId);
          if (!template) return sum;
          const economics = selectionEconomics(template, selection.courseCount, selection.capacity);
          return {
            courseCount: sum.courseCount + selection.courseCount,
            patientCount: sum.patientCount + economics.patientCount,
            revenue: sum.revenue + economics.revenue
          };
        },
        { courseCount: 0, patientCount: 0, revenue: 0 }
      ),
    [templates, selections]
  );

  const isPeriodValid = fromWeekNumber <= toWeekNumber;
  const isMaxStaffLoadValid =
    maxStaffLoadPercent >= MAX_STAFF_LOAD_FLOOR_PERCENT &&
    maxStaffLoadPercent <= MAX_STAFF_LOAD_CEILING_PERCENT;
  const canRun =
    selections.length > 0 && !hasIncompleteSelection && isPeriodValid && isMaxStaffLoadValid;

  const buildInput = useCallback(
    (staff: readonly StaffResponse[], bedCount: number): YearPlanSimulationInput => ({
      yearPlan,
      templates,
      staff,
      bedCount,
      selections,
      fromWeekNumber,
      toWeekNumber,
      maxStaffLoadPercent
    }),
    [yearPlan, templates, selections, fromWeekNumber, toWeekNumber, maxStaffLoadPercent]
  );

  return {
    templates,
    getRow,
    updateRow,
    fromWeekNumber,
    toWeekNumber,
    setFromWeekNumber,
    setToWeekNumber,
    maxStaffLoadPercent,
    setMaxStaffLoadPercent,
    isPeriodValid,
    isMaxStaffLoadValid,
    hasIncompleteSelection,
    totals,
    canRun,
    buildInput
  };
}
